import * as React from "react";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Search as SearchIcon, Settings, Eye, TableProperties, RotateCcw } from "lucide-react";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { QUERY_CONFIG } from "../../constants/QueryConfig";
import { FilterCombobox } from "../common/FilterCombobox";
import TablePagination from "../common/Table/TablePagination";
import { useTableHandlers } from "../common/Table/Hooks/UseTableHandlers";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import FlaggedPathsRepository from "../../repositories/FlaggedPathsRepository";
import {
  FlaggedPathReason,
  FlaggedPathReasonLabels,
  FlaggedPathStatus,
  FlaggedPathStatusLabels,
  FlaggedPathSummary,
} from "../../models/FlaggedPath";
import { useAuth } from "../../contexts/AuthContext";
import { useOrganization } from "../../contexts/OrganizationContext";
import { MigrationTypeLabels, MigrationType } from "../../models/Statistics/MigrationType";
import { FlaggedPathModal } from "./FlaggedPathModal";
import { SearchHighlight } from "../common/SearchHighlight";
import { cn } from "@/lib/utils";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import {
  fileFiltersBoxClassName,
  filterComboboxClassName,
  filterSearchClassName,
  tableWrapperClassName,
} from "../common/Table/TableStyles";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import TableEmptyState from "../common/TableEmptyState";

interface FlaggedPathsTableProps {
  initialSearchTerm?: string;
  newFlaggedPathsForExport: FlaggedPathSummary[];
  setNewFlaggedPathsForExport: React.Dispatch<React.SetStateAction<FlaggedPathSummary[]>>;
}

const statusOptions = Object.entries(FlaggedPathStatusLabels).map(([value, label]) => ({ value, label }));

const recurredOptions = [
  { value: "recurred", label: "Came back", icon: RotateCcw },
  { value: "steady", label: "Never cleared" },
];

const reasonOptions = Object.entries(FlaggedPathReasonLabels).map(([value, label]) => ({ value, label }));

// Every migration type, folder discoveries included. A case-folders discovery stores its occurrences under
// CaseFoldersMigration — CreateOrUpdateFlaggedPathCommandHandler.GetEntity resolves it, the cycle-end sweep
// switches on it, and the backfill maps it — and the filter matches the stored type exactly, so leaving the two
// folder types out of the dropdown made those occurrences unfindable rather than tidier.
const entityTypeOptions = Object.entries(MigrationTypeLabels).map(([value, label]) => ({ value, label }));

/** How much of a firm is held up by one folder, and whether it has been away and come back. */
const OccurrencesCell: React.FC<{ path: FlaggedPathSummary }> = ({ path }) => {
  if (path.liveOccurrenceCount === 0) {
    return (
      <span className="text-sm text-muted-foreground">
        {path.clearedOccurrenceCount > 0 ? `${path.clearedOccurrenceCount} cleared` : "None"}
      </span>
    );
  }

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Badge variant="neutral">
                {path.liveEntityCount === 1 ? "1 entity" : `${path.liveEntityCount} entities`}
              </Badge>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {`${path.liveOccurrenceCount} source ${path.liveOccurrenceCount === 1 ? "record" : "records"} across `
              + `${path.liveEntityCount} ${path.liveEntityCount === 1 ? "entity" : "entities"}`}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {path.recurredOccurrenceCount > 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Badge variant="warning" className="gap-1">
                  <RotateCcw className="size-3" />
                  Came back
                </Badge>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {`${path.recurredOccurrenceCount} of them were cleared and are being reported again`}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

export const FlaggedPathsTable: React.FC<FlaggedPathsTableProps> = ({
  initialSearchTerm = "",
  newFlaggedPathsForExport,
  setNewFlaggedPathsForExport,
}) => {
  // Built during render, not inside queryFn: useApi() calls React hooks, so constructing a repository outside a
  // render invokes them out of context and the query silently yields nothing.
  const flaggedPathsRepository = new FlaggedPathsRepository();
  const queryClient = useQueryClient();
  const { getUserInfo } = useAuth();
  const { selectedOrganizationId } = useOrganization();
  const userInfo = getUserInfo();
  const canEdit = userInfo.isAdmin() || userInfo.isEditor();

  // The page opens on outstanding work. Expressed here rather than as a server default, so that asking for
  // everything stays possible — an undefaulted filter cannot tell "omitted" from "all".
  const [statusFilter, setStatusFilter] = useState<FlaggedPathStatus | null>(FlaggedPathStatus.Live);
  const [recurredFilter, setRecurredFilter] = useState<string | null>(null);
  const [entityTypeFilter, setEntityTypeFilter] = useState<MigrationType | null>(null);
  const [reasonFilter, setReasonFilter] = useState<FlaggedPathReason | null>(null);
  const [searchQuery, setSearchQuery] = useState(initialSearchTerm);
  const [selectedPath, setSelectedPath] = useState<FlaggedPathSummary | null>(null);

  const { order, page, rowsPerPage, rows, changeOrder, handleRowsPerPageChange, setPage, setRows } =
    useTableHandlers({ order: "desc", sort: "lastSeenOn", page: 0, rowsPerPage: 50 });

  const flaggedPathsQuery = useQuery({
    queryKey: [
      QUERY_KEYS.FLAGGED_PATHS,
      selectedOrganizationId,
      statusFilter,
      recurredFilter,
      entityTypeFilter,
      reasonFilter,
      searchQuery,
      page,
      rowsPerPage,
      order,
    ],
    queryFn: () =>
      flaggedPathsRepository.getFlaggedPaths(selectedOrganizationId!, {
        status: statusFilter,
        recurred: recurredFilter === null ? null : recurredFilter === "recurred",
        migrationType: entityTypeFilter,
        reason: reasonFilter,
        search: searchQuery || null,
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        descending: order === "desc",
      }),
    staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
    refetchInterval: QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
    refetchOnMount: true,
    enabled: !!selectedOrganizationId,
  });

  const flaggedPaths = flaggedPathsQuery.data?.data ?? [];
  const totalCount = flaggedPathsQuery.data?.paging?.totalCount ?? 0;

  useEffect(() => {
    setRows(totalCount);
  }, [totalCount, setRows]);

  const handleSearch = (newSearchQuery: string) => {
    setSearchQuery(newSearchQuery || "");
    setPage(0);
  };

  // Kept apart from isError on purpose. Folding the two dims the table and suppresses the empty state, which
  // between them leave a failed load looking like a permanently loading one — no message, no way to retry.
  const loading = flaggedPathsQuery.isFetching;
  const failed = flaggedPathsQuery.isError;
  const disabledRows = new Set(newFlaggedPathsForExport.map((p) => p.id));

  const handleAddFlaggedPathForExport = (flaggedPath: FlaggedPathSummary) => {
    if (!disabledRows.has(flaggedPath.id)) {
      setNewFlaggedPathsForExport((prev) => [...prev, flaggedPath]);
    }
  };

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      <div className={fileFiltersBoxClassName}>
        <div className={filterSearchClassName}>
          <Label className="text-xs font-semibold text-muted-foreground whitespace-nowrap">Search flagged paths</Label>
          <div className="relative">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Path, case number, source record…"
              value={searchQuery}
              onChange={(event) => handleSearch(event.target.value)}
            />
          </div>
        </div>
        <div className={filterComboboxClassName}>
          <Label className="text-xs font-semibold text-muted-foreground">Status</Label>
          <FilterCombobox
            value={statusFilter}
            onChange={(newValue) => { setPage(0); setStatusFilter(newValue as FlaggedPathStatus | null); }}
            placeholder="Any"
            options={statusOptions}
          />
        </div>
        <div className={filterComboboxClassName}>
          <Label className="text-xs font-semibold text-muted-foreground">Recurrence</Label>
          <FilterCombobox
            value={recurredFilter}
            onChange={(newValue) => { setPage(0); setRecurredFilter(newValue); }}
            placeholder="Any"
            options={recurredOptions}
          />
        </div>
        <div className={filterComboboxClassName}>
          <Label className="text-xs font-semibold text-muted-foreground">Entity type</Label>
          <FilterCombobox
            value={entityTypeFilter}
            onChange={(newValue) => { setPage(0); setEntityTypeFilter(newValue as MigrationType | null); }}
            placeholder="Any"
            options={entityTypeOptions}
          />
        </div>
        <div className={filterComboboxClassName}>
          <Label className="text-xs font-semibold text-muted-foreground">Reason</Label>
          <FilterCombobox
            value={reasonFilter}
            onChange={(newValue) => { setPage(0); setReasonFilter(newValue as FlaggedPathReason | null); }}
            placeholder="Any"
            options={reasonOptions}
          />
        </div>
      </div>

      <div className={cn(tableWrapperClassName, loading && !failed && "opacity-50 pointer-events-none")}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[38%]">Path</TableHead>
              <TableHead className="w-[14%]">Affects</TableHead>
              <TableHead className="w-[12%]">Discovery</TableHead>
              <TableHead className="w-[10%]">Status</TableHead>
              <TableHead className="w-[12%]">
                <SortableTableColumnHeader
                  label="Last seen"
                  sortProperty="lastSeenOn"
                  getSort={() => "lastSeenOn"}
                  setSort={() => undefined}
                  getOrder={() => order}
                  setOrder={(newOrder) => { setPage(0); changeOrder(newOrder); }}
                />
              </TableHead>
              <TableHead className="w-[60px]">{canEdit ? "Manage" : "View"}</TableHead>
              {canEdit && <TableHead className="w-[60px]">Queue for CSV export</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {flaggedPaths.map((flaggedPath) => (
              <TableRow key={flaggedPath.id}>
                <TableCell className="whitespace-normal break-all">
                  <SearchHighlight text={flaggedPath.path} searchTerm={searchQuery} level="body-sm" />
                </TableCell>
                <TableCell>
                  <OccurrencesCell path={flaggedPath} />
                </TableCell>
                <TableCell>
                  <Badge variant={flaggedPath.allowDiscoverySystemWide ? "success" : "danger"}>
                    {flaggedPath.allowDiscoverySystemWide ? "Allowed" : "Blocked"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={flaggedPath.status === FlaggedPathStatus.Live ? "warning" : "neutral"}>
                    {FlaggedPathStatusLabels[flaggedPath.status]}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className="text-sm">
                    {flaggedPath.lastSeenOn ? new Date(flaggedPath.lastSeenOn).toLocaleDateString() : "—"}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex flex-row gap-1">
                    {canEdit ? (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-primary"
                        aria-label="Manage"
                        onClick={() => setSelectedPath(flaggedPath)}
                      >
                        <Settings className="size-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="icon-sm"
                        aria-label="View Occurrences"
                        onClick={() => setSelectedPath(flaggedPath)}
                      >
                        <Eye className="size-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
                {canEdit && (
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="text-green-600 hover:text-green-700"
                      aria-label="Queue for CSV export"
                      disabled={disabledRows.has(flaggedPath.id)}
                      onClick={() => handleAddFlaggedPathForExport(flaggedPath)}
                    >
                      <TableProperties className="size-4" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {failed && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={99}>
                  <TableEmptyState
                    icon={AlertTriangle}
                    title="Failed to load flagged paths"
                    description="The list could not be fetched. Nothing has changed."
                    action={
                      <Button variant="outline" size="sm" onClick={() => flaggedPathsQuery.refetch()}>
                        Retry
                      </Button>
                    }
                  />
                </TableCell>
              </TableRow>
            )}
            {flaggedPaths.length === 0 && !loading && !failed && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={99}>
                  <TableEmptyState
                    title="No flagged paths found"
                    description={
                      statusFilter === FlaggedPathStatus.Live
                        ? "Nothing outstanding. Clear the status filter to see paths that have settled."
                        : undefined
                    }
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          rows={rows}
          onPageChange={setPage}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </div>

      {selectedPath && selectedOrganizationId && (
        <FlaggedPathModal
          open={selectedPath !== null}
          flaggedPath={selectedPath}
          organizationId={selectedOrganizationId}
          searchTerm={searchQuery}
          onClose={() => setSelectedPath(null)}
          onSave={async (updatedPath) => {
            await flaggedPathsRepository.updateFlaggedPath(selectedOrganizationId, selectedPath.id, updatedPath);
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FLAGGED_PATHS, selectedOrganizationId] }),
              queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FLAGGED_PATH_OCCURRENCES, selectedOrganizationId] }),
              queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION, selectedOrganizationId] }),
            ]);
            setSelectedPath(null);
          }}
        />
      )}
    </div>
  );
};
